/**
 * LoginPage — Apple-style sign-in screen for CA Statement Engine.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { signIn, user } = useAuth();
  const navigate    = useNavigate();

  // Already logged in → skip login page entirely
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: signInError } = await signIn(email.trim(), password);
    setLoading(false);

    if (signInError) {
      setError('Invalid email or password. Please check your credentials.');
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-slate-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200/60 dark:border-slate-700/60 px-8 py-10">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-sky-600 flex items-center justify-center shadow-md mb-4">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-[22px] font-semibold text-[#1D1D1F] dark:text-slate-100 tracking-tight">
            CA Statement Engine
          </h1>
          <p className="text-sm text-[#6E6E73] dark:text-slate-400 mt-1">
            Sign in to your account
          </p>
        </div>

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Email address
            </label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3.5 py-2.5 rounded-xl text-sm bg-[#F5F5F7] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[#1D1D1F] dark:text-slate-100 placeholder:text-[#AEAEB2] dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-150"
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#1D1D1F] dark:text-slate-300 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 pr-10 rounded-xl text-sm bg-[#F5F5F7] dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[#1D1D1F] dark:text-slate-100 placeholder:text-[#AEAEB2] dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-150"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6E73] dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full mt-2 py-2.5 rounded-xl text-sm font-semibold bg-sky-600 hover:bg-sky-700 active:scale-[0.98] text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2 transition-all duration-150"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-[13px] text-[#6E6E73] dark:text-slate-400">
          Don't have an account?{' '}
          <Link to="/signup" className="text-sky-600 dark:text-sky-400 font-medium hover:underline">
            Create one
          </Link>
        </p>
      </div>
      <p className="mt-6 text-[12px] text-[#AEAEB2] dark:text-slate-600">
        CA Statement Engine © {new Date().getFullYear()}
      </p>
    </div>
  );
}
